import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import RatingScale from "./RatingScale";
import evaluationCategories from "../../utils/EvaluationCategories";
import SkillsRating from "./SkillsRating";
import FeedbackRating from "./FeedbackRating";
import { fetchVoteDetailsAsync } from "../../redux/actions/anketAction";
import { completeVotingAsync } from "../../redux/actions/completeVotingAction";
import fetchPendingVotesAsync from "../../redux/actions/votingFormAction";
import { deleteNotification } from "../../redux/actions/bildirimActions";
import { toast } from "react-toastify";

const RATING_MAPPINGS = {
  "Zaman yönetimi ve iş teslimatları": "timeControl",
  "İletişim ve takım çalışması": "communication",
  "Problem çözme ve yaratıcılık": "problemSolving",
  "Geri bildirim ve kişisel gelişim": "improvement",
  "Bağımsız Çalışabilme ve İnisiyatif": "initiative",
};

const Anket = ({ setRatingIsOpen, voteId }) => {
  const dispatch = useDispatch();
  const [ratings, setRatings] = useState({});
  const [voteDetails, setVoteDetails] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [discardWanted, setDiscardWanted] = useState(false);
  const [discardReason, setDiscardReason] = useState("");
  const { pendingVotes } = useSelector((state) => state.votingForm);
  const { loading, success } = useSelector((state) => state.completeVoting);
  const { notifications } = useSelector((state) => state.bildirim);

  const currentVote = pendingVotes?.data?.find(
    (vote) => vote.voteId === voteId
  );

  useEffect(() => {
    const fetchVoteDetails = async () => {
      if (!voteId) return;

      try {
        const result = await dispatch(fetchVoteDetailsAsync(voteId)).unwrap();
        setVoteDetails(result.data);
      } catch (error) {
        console.error("Error fetching vote details:", error);
      }
    };

    fetchVoteDetails();
  }, [dispatch, voteId]);

  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalStyle;
      document.documentElement.style.overflow = originalStyle;
    };
  }, []);

  useEffect(() => {
    if (success) {
      setRatingIsOpen({ isOpen: false, voteId: null });
    }
  }, [success, setRatingIsOpen]);

  const handleRatingChange = (category, value) => {
    setRatings((prev) => ({ ...prev, [category]: value }));
  };

  const handleSubmit = async () => {
    try {
      // Validation - kategori başlıklarını kullanarak kontrol et
      const requiredCategories = Object.keys(RATING_MAPPINGS);
      const missingScores = requiredCategories.filter(
        (category) => !ratings[category]
      );

      if (missingScores.length > 0) {
        toast.error("Lütfen tüm değerlendirme kategorilerini doldurun.");
        return;
      }

      // Skorları doğru formatta hazırla
      const scores = {};
      Object.entries(RATING_MAPPINGS).forEach(([category, key]) => {
        scores[key] = ratings[category] || 0;
      });

      const voteData = {
        scores,
        feedback: feedback || "",
        discardWanted: discardWanted,
        discardReason: discardReason || "",
        skillVotes: {},
      };

      // Eğer skills varsa onları da ekle
      if (voteDetails?.skills) {
        voteDetails.skills.forEach((skill) => {
          voteData.skillVotes[skill.name] = ratings[skill.name] || 0;
        });
      }

      const result = await dispatch(
        completeVotingAsync({ voteId, voteData })
      ).unwrap();

      if (result) {
        // İlgili bildirimi bul ve sil
        const relatedNotification = notifications.find(
          (notification) =>
            notification.type === "PROJECT_USER_VOTETIME" &&
            notification.content?.voteId === voteId
        );

        if (relatedNotification) {
          await dispatch(deleteNotification(relatedNotification._id)).unwrap();
        }

        toast.success("Değerlendirme başarıyla gönderildi");
        await dispatch(fetchPendingVotesAsync()).unwrap();
        setRatingIsOpen({ isOpen: false, voteId: null });
        window.location.reload();
      }
    } catch (error) {
      console.error("Değerlendirme gönderilirken hata:", error);
      toast.error(
        error.message || "Değerlendirme gönderilirken bir hata oluştu"
      );
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-whiteBg/80 z-[999] p-4 pointer-events-none overflow-y-auto">
      <div className="my-20 border-2 border-lineGray rounded-2xl w-full max-w-[846px] bg-whiteBg shadow-lg flex flex-col pointer-events-auto">
        <div className="sticky top-0 z-10 p-2 md:p-4 w-full border-b-2 border-lineGray ">
          <div className="flex justify-between py-2 md:py-3 px-3 md:px-6">
            <p className="text-lg md:text-xl font-medium text-darkGreen">
              {currentVote?.targetUser?.name} Haftalık Değerlendirme Anketi
            </p>
            <button
              onClick={() => setRatingIsOpen({ isOpen: false, voteId: null })}
              className="p-1"
            >
              <img src="/Carpig.svg" alt="Kapat" className="w-6 h-6" />
            </button>
          </div>
        </div>
        <div
          className="flex-1 overflow-y-auto py-2 md:py-3 px-3 md:px-6 divide-y-2 divide-lineGray max-h-[calc(100vh-200px)]"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "grayy",
            overflowX: "hidden",
          }}
        >
          <div className="flex flex-col gap-3 md:gap-4 divide-y-2 divide-lineGray">
            {evaluationCategories.map((category, index) => (
              <div key={index} className="p-3 md:p-4">
                <h3 className="text-base md:text-lg font-medium text-dark py-1 md:py-2">
                  • {category.title}
                </h3>
                <p className="text-xs md:text-sm text-grayy font-normal py-1 md:py-2">
                  {category.description}
                </p>
                <RatingScale
                  category={category.title}
                  selected={ratings[category.title] || null}
                  handleRatingChange={handleRatingChange}
                />
              </div>
            ))}
            {voteDetails?.skills && voteDetails.skills.length > 0 && (
              <SkillsRating
                skills={voteDetails.skills}
                ratings={ratings}
                handleRatingChange={handleRatingChange}
              />
            )}
            <div>
              <FeedbackRating
                value={feedback}
                onChange={setFeedback}
                discardWanted={discardWanted}
                onDiscardChange={setDiscardWanted}
                discardReason={discardReason}
                onDiscardReasonChange={setDiscardReason}
              />
              <div className="flex justify-center my-4 md:mb-6">
                <button
                  className="btn-green h-[48px] md:h-[56px] w-[200px] md:w-[252px] text-sm"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? "Gönderiliyor..." : "Değerlendirmeyi Gönder"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Anket;
